import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { USER_CHANNEL } from "@/lib/constant";
import http from "@/lib/http";

export interface User {
  bindphone: string;
  mobilephone: number;
  nickname: string;
  unreadMessages: number;
  userLogin: string;
  userStatus: number;
  email: string;
  roles?: string[];
  permissions?: string[];
  userId?: number;
  userRoles?: {
    enterpriseId: number;
    roleId: number;
    roleName: string;
    userId: number;
    userRoleId: number;
  }[];
  status?: number;
  info?: string;
  code?: string;
  channel?: USER_CHANNEL;
}

export const useUserStore = defineStore("user", () => {
  // 状态
  const _userInfo = ref<User>(); // 用户信息

  // 计算属性
  const userInfo = computed(() => _userInfo.value);
  const logined = computed(() => !!userInfo.value?.userLogin);
  const avatarUrl = computed(
    () =>
      `${import.meta.env.VITE_API_BASE_URL || "/c4c3"}/personal/getUserAvator`
  );
  const layoutInfo = computed(() => ({ inIframe: window.self !== window.top }));

  // 方法
  async function ping() {
    const resUser = await http.get<User>("/account/ping");
    if (resUser.code === "200") {
      _userInfo.value = resUser;
      // 更新localStorage中的用户信息
      updateLocalStorage();
      return true;
    }
    return false;
  }

  // 验证登录状态是否仍然有效
  async function validateLoginStatus() {
    try {
      // 尝试ping服务器验证登录状态
      const isValid = await ping();
      if (!isValid) {
        // 如果ping失败，清除用户信息
        _userInfo.value = undefined;
        localStorage.removeItem("userInfo");
      }
      return isValid;
    } catch (error) {
      // 如果出现错误（如401），也认为登录已过期
      _userInfo.value = undefined;
      localStorage.removeItem("userInfo");
      return false;
    }
  }

  async function login(username: string, password: string) {
    const params = new URLSearchParams();
    params.append("username", username);
    params.append("password", btoa(password));
    const userData = await http.post<User & { message: string }>(
      "/account/login",
      params
    );
    if (userData.userLogin != null) {
      _userInfo.value = userData;
      // 更新localStorage中的用户信息
      updateLocalStorage();
      return { res: true };
    } else {
      return { res: false, message: userData.message };
    }
  }

  async function logout() {
    try {
      await http.get("/account/logout");
      _userInfo.value = undefined;
      // 清除localStorage中的用户信息
      localStorage.removeItem("userInfo");
      return true;
    } catch (error) {
      console.error("Logout error:", error);
      return false;
    }
  }

  // 更新localStorage中的用户信息
  function updateLocalStorage() {
    if (_userInfo.value && _userInfo.value.userLogin) {
      _userInfo.value.channel = _userInfo.value.userLogin.startsWith("WECHAT-")
        ? USER_CHANNEL.WECHAT
        : USER_CHANNEL.EMAIL;
      localStorage.setItem("userInfo", JSON.stringify(_userInfo.value));
    } else {
      localStorage.removeItem("userInfo");
    }
  }

  // 强制更新登录状态，用于响应外部登录状态变更事件
  function forceUpdateLoginStatus() {
    const userInfoStr = localStorage.getItem("userInfo");
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        if (userInfo.userLogin) {
          _userInfo.value = userInfo;
        } else {
          _userInfo.value = undefined;
        }
      } catch (error) {
        console.error("解析用户信息失败:", error);
        _userInfo.value = undefined;
        localStorage.removeItem("userInfo");
      }
    } else {
      _userInfo.value = undefined;
    }
  }

  return {
    // 计算属性
    userInfo,
    logined,
    avatarUrl,
    layoutInfo,
    // 方法
    ping,
    login,
    logout,
    validateLoginStatus,
    forceUpdateLoginStatus,
  };
});
